import SparkMD5 from 'spark-md5';
import { measurePerformance } from '@/shared/lib/performance';
import { uploadWithRecovery } from '@/features/angle';
import { getDeviceCapabilities } from '@/shared/lib/device';
import { createLogger } from '@/shared/lib/logger';

import type { UploadType } from '@/shared/constants';
import type { UploadPolicy } from '@/types/project-reference';

const logger = createLogger('ImageUploadService');

export interface ImageUploadOptions {
  type: UploadType;
  maxFiles?: number;
  allowCrop?: boolean;
  onProgress?: (progress: UploadProgress) => void;
  onCleanupFailure?: (id: string) => Promise<void>;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface ProcessedFile {
  file: File;
  md5: string;
  width: number;
  height: number;
}

export interface UploadResult<TConfirmed = unknown> {
  id: string;
  url: string;
  type: UploadType;
  confirmed?: TConfirmed;
}

export interface BatchUploadOptions extends ImageUploadOptions {
  mainAngleIndex?: number;
  /** Real S3 byte progress per file (index into the batch's file array). */
  onFileProgress?: (fileIndex: number, progress: UploadProgress) => void;
}

export type ConfirmCallback<TConfirmed> = (
  id: string
) => Promise<TConfirmed>;

export class ImageUploadService {
  // Cap on parallel presigned PUTs — 10 concurrent uploads saturate mobile
  // uplinks and starve each other's progress; 3 matches the retry budget UX.
  private static readonly MAX_CONCURRENT_UPLOADS = 3;
  private static readonly TARGET_MEGAPIXELS = 1;
  private static readonly PIXEL_MULTIPLE = 16;
  private static readonly INITIAL_QUALITY = 0.85;
  private static readonly QUALITY_STEP = 0.1;
  private static readonly MAX_FILE_SIZE = 900 * 1024; // 900KB
  private static readonly MIN_QUALITY = 0.3;

  // Angle-specific: 2048px longest side, 1.5MB max
  private static readonly MAX_ANGLE_DIMENSION = 2048;
  private static readonly MAX_ANGLE_FILE_SIZE = 1500 * 1024; // 1.5MB

  // Reference-specific: 768px longest side (references only inform style/colour/
  // material, so they don't need angle-grade resolution; smaller uploads cut
  // bandwidth + downstream LLM image-token cost). File size stays at 900KB.
  private static readonly MAX_REFERENCE_DIMENSION = 768;

  /**
   * Process and upload a single image file. If `confirm` is provided, it runs
   * after S3 success and must resolve before the upload is considered complete.
   * Failures at any stage (validate, create, S3, confirm) trigger
   * `onCleanupFailure` when the server record already exists.
   */
  async uploadSingle<TConfirmed = unknown>(
    file: File,
    options: ImageUploadOptions,
    apiCallback: (
      processedFile: ProcessedFile
    ) => Promise<{ id: string; uploadPolicy: UploadPolicy }>,
    confirm?: ConfirmCallback<TConfirmed>
  ): Promise<UploadResult<TConfirmed>> {
    const processedFile = await this.processImageFile(file, options);
    const { id, uploadPolicy } = await apiCallback(processedFile);

    try {
      // Server-side dedupe: when the API reports that an identical object
      // (matched by md5) already exists in R2, skip the S3 PUT and jump
      // straight to confirm. Removing this early-return would force a
      // redundant upload on every re-submission of the same file.
      if (!uploadPolicy.skip_upload) {
        await this.uploadToS3(
          processedFile.file,
          uploadPolicy,
          options.onProgress
        );
      } else if (options.onProgress) {
        options.onProgress({
          loaded: processedFile.file.size,
          total: processedFile.file.size,
          percentage: 100,
        });
      }

      const confirmed = confirm ? await confirm(id) : undefined;

      return {
        id,
        url: uploadPolicy.url,
        type: options.type,
        ...(confirmed !== undefined ? { confirmed } : {}),
      } as UploadResult<TConfirmed>;
    } catch (error) {
      logger.debug('Upload failed after record creation, cleaning up:', error);
      await this.runCleanup(options, id);
      throw error;
    }
  }

  /**
   * Process and upload multiple image files. If `confirm` is provided, it runs
   * per-item after S3 success. Confirm failure is treated like an S3 failure
   * for that item (cleanup + excluded from the result).
   */
  async uploadBatch<TConfirmed = unknown>(
    files: File[],
    options: BatchUploadOptions,
    apiCallback: (
      processedFiles: ProcessedFile[],
      mainIndex?: number
    ) => Promise<Array<{ id: string; uploadPolicy: UploadPolicy }>>,
    confirm?: ConfirmCallback<TConfirmed>
  ): Promise<UploadResult<TConfirmed>[]> {
    const processedFiles = await Promise.all(
      files.map((file) => this.processImageFile(file, options))
    );

    const uploadData = await apiCallback(
      processedFiles,
      options.mainAngleIndex
    );

    const uploadOne = async (i: number) => {
      const processedFile = processedFiles[i];
      const data = uploadData[i];
      if (!processedFile || !data) {
        throw new Error(`No upload data for file ${i}`);
      }
      const { id, uploadPolicy } = data;

      try {
        // Server-side dedupe (batch): skip S3 PUT when the API reports an
        // existing object for this md5. See uploadSingle for the single-file
        // counterpart.
        if (!uploadPolicy.skip_upload) {
          await this.uploadToS3(processedFile.file, uploadPolicy, (progress) =>
            options.onFileProgress?.(i, progress)
          );
        } else {
          options.onFileProgress?.(i, {
            loaded: processedFile.file.size,
            total: processedFile.file.size,
            percentage: 100,
          });
        }

        const confirmed = confirm ? await confirm(id) : undefined;

        return {
          id,
          url: uploadPolicy.url,
          type: options.type,
          confirmed,
          success: true as const,
        };
      } catch (error) {
        console.error(`Failed to upload file ${i}:`, error);
        return {
          id,
          url: '',
          type: options.type,
          success: false as const,
          error,
        };
      }
    };

    // Bounded worker pool — at most MAX_CONCURRENT_UPLOADS PUTs in flight.
    const uploadResults: Array<Awaited<ReturnType<typeof uploadOne>>> =
      new Array(processedFiles.length);
    let nextIndex = 0;
    const workerCount = Math.min(
      ImageUploadService.MAX_CONCURRENT_UPLOADS,
      processedFiles.length
    );
    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        for (;;) {
          const i = nextIndex++;
          if (i >= processedFiles.length) return;
          uploadResults[i] = await uploadOne(i);
        }
      })
    );

    const successfulResults = uploadResults.filter((r) => r.success);
    const failedIds = uploadResults.filter((r) => !r.success).map((r) => r.id);

    if (failedIds.length > 0) {
      logger.info(`Cleaning up ${failedIds.length} failed uploads`);
      await Promise.all(failedIds.map((id) => this.runCleanup(options, id)));
    }

    if (successfulResults.length === 0) {
      throw new Error('All uploads failed');
    }

    return successfulResults.map((r) => ({
      id: r.id,
      url: r.url,
      type: r.type,
      ...(r.confirmed !== undefined ? { confirmed: r.confirmed } : {}),
    })) as UploadResult<TConfirmed>[];
  }

  private async runCleanup(
    options: ImageUploadOptions,
    id: string
  ): Promise<void> {
    if (!options.onCleanupFailure) return;
    try {
      await options.onCleanupFailure(id);
      logger.info('Cleaned up failed upload record:', id);
    } catch (err) {
      console.error('Failed to cleanup record after upload failure:', err);
    }
  }

  /**
   * Process a single file according to specifications
   * Handles both images and non-image files (PDFs, text files)
   */
  private async processImageFile(
    file: File,
    options?: ImageUploadOptions
  ): Promise<ProcessedFile> {
    return measurePerformance('processImageFile', async () => {
      // Check if file is a non-image type (PDF, text, etc.)
      const isNonImageFile = !file.type.startsWith('image/');

      if (isNonImageFile) {
        // For non-image files (PDFs, text files), skip image processing
        // Just calculate MD5 and return the file as-is
        logger.debug('Processing non-image file:', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });

        const md5 = await this.calculateMD5(file);

        return {
          file,
          md5,
          width: 0,
          height: 0,
        };
      }

      // Get dimensions first to check if already at target size
      const dimensions = await this.getImageDimensions(file);

      // Check if file is already processed (correct dimensions and format)
      const isAngle = options?.type === 'angle';
      const is16Aligned =
        dimensions.width % ImageUploadService.PIXEL_MULTIPLE === 0 &&
        dimensions.height % ImageUploadService.PIXEL_MULTIPLE === 0;
      const pixelCount = dimensions.width * dimensions.height;
      // Angles + references use a longest-side cap; controls keep the 1MP
      // (megapixel-area) target.
      const maxLongSide = this.resolveMaxLongSide(options?.type);
      const isCorrectSize = maxLongSide
        ? Math.max(dimensions.width, dimensions.height) <= maxLongSide &&
          is16Aligned
        : pixelCount <= 1000000 * 1.1 &&
          pixelCount >= 1000000 * 0.9 &&
          is16Aligned;
      const maxFileSize = isAngle
        ? ImageUploadService.MAX_ANGLE_FILE_SIZE
        : ImageUploadService.MAX_FILE_SIZE;
      // Controls must be PNG (transparency), other images - JPEG
      const acceptedFormats =
        options?.type === 'control' ? ['image/png'] : ['image/jpeg'];
      const isAlreadyProcessed =
        acceptedFormats.includes(file.type) &&
        file.size <= maxFileSize &&
        isCorrectSize;

      logger.debug('Processing file:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        dimensions: `${dimensions.width}x${dimensions.height}`,
        pixelCount,
        isCorrectSize,
        isAlreadyProcessed,
        uploadType: options?.type,
      });

      // If already processed (from crop), use it directly
      // Otherwise, convert and resize image based on type
      let processedFile: File;
      if (isAlreadyProcessed) {
        processedFile = file;
      } else if (options?.type === 'control') {
        // Controls must be PNG to preserve transparency
        processedFile = await this.resizePngImage(file);
      } else {
        // All other images (angles, references) are JPEG
        processedFile = await this.convertToOptimizedJpeg(file, options);
      }

      // Calculate MD5 hash of the final processed file
      const md5 = await this.calculateMD5(processedFile);

      // Get final dimensions if we processed the file
      const finalDimensions = isAlreadyProcessed
        ? dimensions
        : await this.getImageDimensions(processedFile);

      return {
        file: processedFile,
        md5,
        width: finalDimensions.width,
        height: finalDimensions.height,
      };
    });
  }

  /**
   * Longest-side cap per upload type. Angles: 2048px, references: 768px.
   * Controls return undefined and fall back to the 1MP (area) target.
   */
  private resolveMaxLongSide(type?: UploadType): number | undefined {
    if (type === 'angle') return ImageUploadService.MAX_ANGLE_DIMENSION;
    if (type === 'reference') return ImageUploadService.MAX_REFERENCE_DIMENSION;
    return undefined;
  }

  /**
   * Convert image to optimized JPEG (angles: 2048px, references: 768px longest
   * side; controls: 1MP)
   */
  private async convertToOptimizedJpeg(
    file: File,
    options?: ImageUploadOptions
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          const maxLongSide = this.resolveMaxLongSide(options?.type);
          const targetDimensions = this.calculateTargetDimensions(
            img.width,
            img.height,
            maxLongSide
          );

          // Create canvas
          const canvas = document.createElement('canvas');
          canvas.width = targetDimensions.width;
          canvas.height = targetDimensions.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          // Set high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image
          ctx.drawImage(
            img,
            0,
            0,
            targetDimensions.width,
            targetDimensions.height
          );

          // Clean up
          URL.revokeObjectURL(url);

          // Convert to JPEG with quality optimization
          const optimizedFile = await this.optimizeJpegQuality(
            canvas,
            file.name,
            options
          );
          resolve(optimizedFile);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Calculate target dimensions, rounded to 16px.
   * With maxLongSide: scale longest side to maxLongSide (only downscale).
   * Without: target 1 megapixel total area.
   */
  private calculateTargetDimensions(
    originalWidth: number,
    originalHeight: number,
    maxLongSide?: number
  ): { width: number; height: number } {
    const pm = ImageUploadService.PIXEL_MULTIPLE;

    if (maxLongSide) {
      const longestSide = Math.max(originalWidth, originalHeight);

      // Only downscale, never upscale
      if (longestSide <= maxLongSide) {
        return {
          width: Math.max(pm, Math.round(originalWidth / pm) * pm),
          height: Math.max(pm, Math.round(originalHeight / pm) * pm),
        };
      }

      const scale = maxLongSide / longestSide;
      let width = Math.round((originalWidth * scale) / pm) * pm;
      let height = Math.round((originalHeight * scale) / pm) * pm;

      // Ensure longest side does not exceed max after rounding
      if (Math.max(width, height) > maxLongSide) {
        if (width >= height) {
          width = Math.floor(maxLongSide / pm) * pm;
          height = Math.max(
            pm,
            Math.round((width * originalHeight) / originalWidth / pm) * pm
          );
        } else {
          height = Math.floor(maxLongSide / pm) * pm;
          width = Math.max(
            pm,
            Math.round((height * originalWidth) / originalHeight / pm) * pm
          );
        }
      }

      return { width: Math.max(pm, width), height: Math.max(pm, height) };
    }

    // Default: 1 megapixel target
    const aspectRatio = originalWidth / originalHeight;
    const targetPixels = ImageUploadService.TARGET_MEGAPIXELS * 1000000;

    let width = Math.sqrt(targetPixels * aspectRatio);
    let height = width / aspectRatio;

    width = Math.round(width / pm) * pm;
    height = Math.round(height / pm) * pm;

    const actualPixels = width * height;
    if (actualPixels > targetPixels) {
      const scale = Math.sqrt(targetPixels / actualPixels);
      width = Math.round((width * scale) / pm) * pm;
      height = Math.round((height * scale) / pm) * pm;
    }

    return { width, height };
  }

  /**
   * Optimize JPEG quality to ensure file size within limit
   */
  private async optimizeJpegQuality(
    canvas: HTMLCanvasElement,
    fileName: string,
    options?: ImageUploadOptions
  ): Promise<File> {
    const fileSizeLimit =
      options?.type === 'angle'
        ? ImageUploadService.MAX_ANGLE_FILE_SIZE
        : ImageUploadService.MAX_FILE_SIZE;
    // Use maximum quality for control images to preserve black levels
    let quality =
      options?.type === 'control' ? 0.95 : ImageUploadService.INITIAL_QUALITY;
    let file: File | null = null;

    while (quality >= ImageUploadService.MIN_QUALITY) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });

      if (!blob) {
        throw new Error('Failed to convert canvas to blob');
      }

      file = new File([blob], fileName.replace(/\.[^/.]+$/, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      if (file.size <= fileSizeLimit) {
        break;
      }

      quality -= ImageUploadService.QUALITY_STEP;
    }

    if (!file) {
      throw new Error('Failed to optimize image quality');
    }

    return file;
  }

  /**
   * Calculate MD5 hash of file
   */
  private async calculateMD5(file: File): Promise<string> {
    return measurePerformance('calculateMD5', async () => {
      const capabilities = getDeviceCapabilities();
      const chunkSize = capabilities.chunkSize;
      const spark = new SparkMD5.ArrayBuffer();

      // For small files, calculate in one go
      if (file.size <= chunkSize) {
        const arrayBuffer = await file.arrayBuffer();
        spark.append(arrayBuffer);
        return spark.end();
      }

      // For larger files, calculate in chunks
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        let currentChunk = 0;
        const chunks = Math.ceil(file.size / chunkSize);

        const loadNext = () => {
          const start = currentChunk * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          fileReader.readAsArrayBuffer(file.slice(start, end));
        };

        fileReader.onload = (e) => {
          if (e.target?.result) {
            spark.append(e.target.result as ArrayBuffer);
            currentChunk++;

            if (currentChunk < chunks) {
              if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => loadNext());
              } else {
                setTimeout(loadNext, 0);
              }
            } else {
              resolve(spark.end());
            }
          }
        };

        fileReader.onerror = reject;
        loadNext();
      });
    });
  }

  /**
   * Upload file to S3/R2 via the presigned PUT URL provided by the server.
   */
  private async uploadToS3(
    file: File,
    uploadPolicy: UploadPolicy,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<void> {
    await uploadWithRecovery(
      file,
      uploadPolicy.url,
      {
        method: 'PUT',
        url: uploadPolicy.url,
        headers: uploadPolicy.headers,
      },
      onProgress
    );
  }

  /**
   * Resize PNG image while preserving transparency for control images
   * Note: PNG files may exceed MAX_FILE_SIZE due to transparency data
   */
  private async resizePngImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          // Calculate target dimensions (1 megapixel, rounded to 16px)
          const targetDimensions = this.calculateTargetDimensions(
            img.width,
            img.height
          );

          // Create canvas
          const canvas = document.createElement('canvas');
          canvas.width = targetDimensions.width;
          canvas.height = targetDimensions.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          // Set high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image on transparent canvas
          ctx.drawImage(
            img,
            0,
            0,
            targetDimensions.width,
            targetDimensions.height
          );

          // Clean up
          URL.revokeObjectURL(url);

          // Convert to PNG (lossless with transparency)
          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(resolve, 'image/png');
          });

          if (!blob) {
            throw new Error('Failed to convert canvas to blob');
          }

          const pngFile = new File(
            [blob],
            file.name.replace(/\.[^/.]+$/, '.png'),
            {
              type: 'image/png',
              lastModified: Date.now(),
            }
          );

          // Log warning if file size exceeds limit (but still allow it for controls)
          if (pngFile.size > ImageUploadService.MAX_FILE_SIZE) {
            logger.debug('PNG control exceeds MAX_FILE_SIZE:', {
              fileName: pngFile.name,
              fileSize: pngFile.size,
              maxSize: ImageUploadService.MAX_FILE_SIZE,
              note: 'PNG transparency may require larger file size',
            });
          }

          resolve(pngFile);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Get image dimensions
   */
  private async getImageDimensions(
    file: File
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();
